import { useInView } from '@/hooks/useInView'
import { TransitionLink } from '@/components/ui/TransitionLink'

/**
 * The four movements of a Burton commission (docs/BRAND-CONTEXT.md).
 * Positions the enquiry flow as a ritual — conversation to delivery —
 * never a checkout.
 */

interface JourneyStep {
  index: string
  duration: string
  title: string
  body: string
}

const JOURNEY_STEPS: JourneyStep[] = [
  {
    index: '01',
    duration: 'Week 0',
    title: 'The conversation',
    body: 'Every commission opens with a conversation, not a cart. Tell us how you ride, where you ride, and what the machine needs to be. You speak directly with the engineer who will build it — no sales layer, no configurator scripts.',
  },
  {
    index: '02',
    duration: 'Weeks 1–2',
    title: 'The composition',
    body: 'Together we compose the machine: geometry shaped to your body and terrain, construction methods chosen for the ride you want, componentry and finish direction set. The atelier builder is where the idea takes form before a single fibre is wound.',
  },
  {
    index: '03',
    duration: 'Weeks 3–10',
    title: 'The build',
    body: 'Additive-manufactured structures, filament-wound carbon tubes, and optimised forged carbon components come together in Cambridge, New Zealand. You receive progress from the workshop as your frame moves from raw material to rolling chassis.',
  },
  {
    index: '04',
    duration: 'Weeks 10–12',
    title: 'The delivery',
    body: 'Your machine leaves the atelier with its serial engraved and its build documented — geometry, construction, and finish on record. One rider, one machine, one of one. Then the only thing left is to ride it.',
  },
]

const STAGGER = [
  '[transition-delay:0ms]',
  '[transition-delay:120ms]',
  '[transition-delay:240ms]',
  '[transition-delay:360ms]',
]

export function CommissionJourneySection() {
  const { ref: headerRef, inView: headerIn } = useInView<HTMLDivElement>(0.15, 'home-journey-header')
  const { ref: stepsRef, inView: stepsIn } = useInView<HTMLDivElement>(0.1, 'home-journey-steps')

  return (
    <section id="commission" className="relative py-32 bg-carbon-900 texture-noise overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
        <div ref={headerRef} className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <p
              className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 transition-all duration-1000 ease-out-expo ${
                headerIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              The Commission
            </p>
            <h2
              className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase leading-tight mb-6 transition-all duration-1000 ease-out-expo [transition-delay:0.1s] ${
                headerIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Dream it.
              <br />
              Build it.
              <br />
              <span className="text-titanium-400">Ride it.</span>
            </h2>
            <p
              className={`text-titanium-400 text-lg leading-relaxed mb-10 transition-all duration-1000 ease-out-expo [transition-delay:0.2s] ${
                headerIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              A Burton is commissioned the way anything singular is commissioned — in
              four movements, from first conversation to first ride.
            </p>
            <TransitionLink
              to="/builder"
              className={`group inline-flex items-center gap-4 border border-gold-500 text-carbon-50 font-display font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-1000 ease-out-expo [transition-delay:0.3s] hover:bg-gold-500 hover:text-carbon-900 ${
                headerIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Begin movement one
              <span aria-hidden className="transition-transform duration-300 ease-out-expo group-hover:translate-x-1.5">
                -&gt;
              </span>
            </TransitionLink>
          </div>
        </div>

        <div ref={stepsRef} className="lg:col-span-8 flex flex-col">
          {JOURNEY_STEPS.map((step, i) => (
            <article
              key={step.index}
              className={`group relative grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-10 md:py-12 border-t border-carbon-700 last:border-b transition-all duration-1000 ease-out-expo ${STAGGER[i]} ${
                stepsIn ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'
              }`}
            >
              <div className="flex flex-col items-center gap-4">
                <span className="font-display font-extrabold text-3xl md:text-4xl text-carbon-600 tabular-nums transition-colors duration-500 group-hover:text-gold-500">
                  {step.index}
                </span>
                <span aria-hidden className="w-px flex-1 bg-carbon-700 group-hover:bg-gold-500/40 transition-colors duration-500" />
              </div>
              <div className="flex flex-col gap-3 pb-2">
                <span className="font-mono text-2xs tracking-[0.3em] uppercase text-titanium-600">
                  {step.duration}
                </span>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-carbon-50 uppercase">
                  {step.title}
                </h3>
                <p className="text-titanium-400 text-base md:text-lg leading-relaxed max-w-2xl">
                  {step.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
